// Updated localization configuration with DataForSEO Labs locations and languages
// Using full string names exactly as provided in the CSV
// Enhanced with aliases and codes for search functionality while preserving exact payload compatibility

export interface Language {
  name: string;
  code: string;           // ISO language code
  aliases: string[];      // Search aliases for improved UX
}

export interface Country {
  name: string;
  code: string;           // ISO country code  
  aliases: string[];      // Search aliases for improved UX
  languages: string[];    // Full language names supported for this location
}

// All languages from DataForSEO Labs data with search aliases
export const languages: Language[] = [
  { name: 'Arabic', code: 'ar', aliases: ['AR', 'Arab'] },
  { name: 'Armenian', code: 'hy', aliases: ['ARM', 'Hy'] },
  { name: 'Azeri', code: 'az', aliases: ['AZ', 'Azerbaijani'] },
  { name: 'Bengali', code: 'bn', aliases: ['BN', 'Bangla'] },
  { name: 'Bosnian', code: 'bs', aliases: ['BS', 'Bosnia'] },
  { name: 'Bulgarian', code: 'bg', aliases: ['BG', 'Bulgar'] },
  { name: 'Chinese (Simplified)', code: 'zh-CN', aliases: ['Chinese', 'Simplified Chinese', 'CN', 'ZH'] },
  { name: 'Chinese (Traditional)', code: 'zh-TW', aliases: ['Traditional Chinese', 'TW', 'HK'] },
  { name: 'Croatian', code: 'hr', aliases: ['HR', 'Croat'] },
  { name: 'Czech', code: 'cs', aliases: ['CS', 'CZ'] },
  { name: 'Danish', code: 'da', aliases: ['DA', 'DK'] },
  { name: 'Dutch', code: 'nl', aliases: ['NL', 'Netherlands'] },
  { name: 'English', code: 'en', aliases: ['EN', 'Eng'] },
  { name: 'Estonian', code: 'et', aliases: ['ET', 'EST'] },
  { name: 'Finnish', code: 'fi', aliases: ['FI', 'Finn'] },
  { name: 'French', code: 'fr', aliases: ['FR', 'Français'] },
  { name: 'German', code: 'de', aliases: ['DE', 'Deutsch'] },
  { name: 'Greek', code: 'el', aliases: ['EL', 'GR'] },
  { name: 'Hebrew', code: 'he', aliases: ['HE', 'IL'] },
  { name: 'Hindi', code: 'hi', aliases: ['HI', 'IN'] },
  { name: 'Hungarian', code: 'hu', aliases: ['HU', 'Magyar'] },
  { name: 'Indonesian', code: 'id', aliases: ['ID', 'Bahasa'] },
  { name: 'Italian', code: 'it', aliases: ['IT', 'Italiano'] },
  { name: 'Japanese', code: 'ja', aliases: ['JA', 'JP'] },
  { name: 'Korean', code: 'ko', aliases: ['KO', 'KR'] },
  { name: 'Latvian', code: 'lv', aliases: ['LV', 'LT'] },
  { name: 'Lithuanian', code: 'lt', aliases: ['LT', 'LV'] },
  { name: 'Macedonian', code: 'mk', aliases: ['MK', 'Macedonia'] },
  { name: 'Malay', code: 'ms', aliases: ['MS', 'MY'] },
  { name: 'Norwegian (Bokmål)', code: 'nb', aliases: ['Norwegian', 'NO', 'NB'] },
  { name: 'Polish', code: 'pl', aliases: ['PL', 'Polski'] },
  { name: 'Portuguese', code: 'pt', aliases: ['PT', 'Português'] },
  { name: 'Romanian', code: 'ro', aliases: ['RO', 'Română'] },
  { name: 'Russian', code: 'ru', aliases: ['RU', 'Русский'] },
  { name: 'Serbian', code: 'sr', aliases: ['SR', 'Serbia'] },
  { name: 'Slovak', code: 'sk', aliases: ['SK', 'Slovakia'] },
  { name: 'Slovenian', code: 'sl', aliases: ['SL', 'Slovenia'] },
  { name: 'Spanish', code: 'es', aliases: ['ES', 'Español'] },
  { name: 'Swedish', code: 'sv', aliases: ['SV', 'SE'] },
  { name: 'Tagalog', code: 'tl', aliases: ['TL', 'Filipino', 'PH'] },
  { name: 'Thai', code: 'th', aliases: ['TH', 'ไทย'] },
  { name: 'Turkish', code: 'tr', aliases: ['TR', 'Türkçe'] },
  { name: 'Ukrainian', code: 'uk', aliases: ['UK', 'UA', 'Українська'] },
  { name: 'Urdu', code: 'ur', aliases: ['UR', 'اردو'] },
  { name: 'Vietnamese', code: 'vi', aliases: ['VI', 'VN', 'Tiếng Việt'] }
];

// All countries with their supported languages from DataForSEO Labs data with search aliases
export const countries: Country[] = [
  { name: 'Algeria', code: 'DZ', aliases: ['DZ'], languages: ['French', 'Arabic'] },
  { name: 'Angola', code: 'AO', aliases: ['AO'], languages: ['Portuguese'] },
  { name: 'Argentina', code: 'AR', aliases: ['AR'], languages: ['Spanish'] },
  { name: 'Armenia', code: 'AM', aliases: ['AM'], languages: ['Armenian'] },
  { name: 'Australia', code: 'AU', aliases: ['AU', 'Aus'], languages: ['English'] },
  { name: 'Austria', code: 'AT', aliases: ['AT'], languages: ['German'] },
  { name: 'Azerbaijan', code: 'AZ', aliases: ['AZ'], languages: ['Azeri'] },
  { name: 'Bahrain', code: 'BH', aliases: ['BH'], languages: ['Arabic'] },
  { name: 'Bangladesh', code: 'BD', aliases: ['BD'], languages: ['Bengali'] },
  { name: 'Belgium', code: 'BE', aliases: ['BE'], languages: ['French', 'Dutch', 'German'] },
  { name: 'Bolivia', code: 'BO', aliases: ['BO'], languages: ['Spanish'] },
  { name: 'Bosnia and Herzegovina', code: 'BA', aliases: ['BA', 'Bosnia'], languages: ['Bosnian'] },
  { name: 'Brazil', code: 'BR', aliases: ['BR'], languages: ['Portuguese'] },
  { name: 'Bulgaria', code: 'BG', aliases: ['BG'], languages: ['Bulgarian'] },
  { name: 'Burkina Faso', code: 'BF', aliases: ['BF'], languages: ['French'] },
  { name: 'Cambodia', code: 'KH', aliases: ['KH'], languages: ['English'] },
  { name: 'Cameroon', code: 'CM', aliases: ['CM'], languages: ['French'] },
  { name: 'Canada', code: 'CA', aliases: ['CA'], languages: ['English', 'French'] },
  { name: 'Chile', code: 'CL', aliases: ['CL'], languages: ['Spanish'] },
  { name: 'Colombia', code: 'CO', aliases: ['CO'], languages: ['Spanish'] },
  { name: 'Costa Rica', code: 'CR', aliases: ['CR'], languages: ['Spanish'] },
  { name: 'Cote d\'Ivoire', code: 'CI', aliases: ['CI', 'Ivory Coast'], languages: ['French'] },
  { name: 'Croatia', code: 'HR', aliases: ['HR'], languages: ['Croatian'] },
  { name: 'Cyprus', code: 'CY', aliases: ['CY'], languages: ['Greek', 'English'] },
  { name: 'Czechia', code: 'CZ', aliases: ['CZ', 'Czech Republic'], languages: ['Czech'] },
  { name: 'Denmark', code: 'DK', aliases: ['DK'], languages: ['Danish'] },
  { name: 'Ecuador', code: 'EC', aliases: ['EC'], languages: ['Spanish'] },
  { name: 'Egypt', code: 'EG', aliases: ['EG'], languages: ['Arabic', 'English'] },
  { name: 'El Salvador', code: 'SV', aliases: ['SV'], languages: ['Spanish'] },
  { name: 'Estonia', code: 'EE', aliases: ['EE'], languages: ['Estonian'] },
  { name: 'Finland', code: 'FI', aliases: ['FI'], languages: ['Finnish'] },
  { name: 'France', code: 'FR', aliases: ['FR'], languages: ['French'] },
  { name: 'Germany', code: 'DE', aliases: ['DE'], languages: ['German'] },
  { name: 'Ghana', code: 'GH', aliases: ['GH'], languages: ['English'] },
  { name: 'Greece', code: 'GR', aliases: ['GR'], languages: ['Greek', 'English'] },
  { name: 'Guatemala', code: 'GT', aliases: ['GT'], languages: ['Spanish'] },
  { name: 'Hong Kong', code: 'HK', aliases: ['HK'], languages: ['English', 'Chinese (Traditional)'] },
  { name: 'Hungary', code: 'HU', aliases: ['HU'], languages: ['Hungarian'] },
  { name: 'India', code: 'IN', aliases: ['IN'], languages: ['English', 'Hindi'] },
  { name: 'Indonesia', code: 'ID', aliases: ['ID'], languages: ['English', 'Indonesian'] },
  { name: 'Ireland', code: 'IE', aliases: ['IE'], languages: ['English'] },
  { name: 'Israel', code: 'IL', aliases: ['IL'], languages: ['Hebrew', 'Arabic'] },
  { name: 'Italy', code: 'IT', aliases: ['IT'], languages: ['Italian'] },
  { name: 'Japan', code: 'JP', aliases: ['JP'], languages: ['Japanese'] },
  { name: 'Jordan', code: 'JO', aliases: ['JO'], languages: ['Arabic'] },
  { name: 'Kazakhstan', code: 'KZ', aliases: ['KZ'], languages: ['Russian'] },
  { name: 'Kenya', code: 'KE', aliases: ['KE'], languages: ['English'] },
  { name: 'Latvia', code: 'LV', aliases: ['LV'], languages: ['Latvian'] },
  { name: 'Lithuania', code: 'LT', aliases: ['LT'], languages: ['Lithuanian'] },
  { name: 'Malaysia', code: 'MY', aliases: ['MY'], languages: ['English', 'Malay'] },
  { name: 'Malta', code: 'MT', aliases: ['MT'], languages: ['English'] },
  { name: 'Mexico', code: 'MX', aliases: ['MX'], languages: ['Spanish'] },
  { name: 'Moldova', code: 'MD', aliases: ['MD'], languages: ['Romanian'] },
  { name: 'Monaco', code: 'MC', aliases: ['MC'], languages: ['French'] },
  { name: 'Morocco', code: 'MA', aliases: ['MA'], languages: ['Arabic', 'French'] },
  { name: 'Myanmar (Burma)', code: 'MM', aliases: ['MM', 'Burma', 'Myanmar'], languages: ['English'] },
  { name: 'Netherlands', code: 'NL', aliases: ['NL', 'Holland'], languages: ['Dutch'] },
  { name: 'New Zealand', code: 'NZ', aliases: ['NZ'], languages: ['English'] },
  { name: 'Nicaragua', code: 'NI', aliases: ['NI'], languages: ['Spanish'] },
  { name: 'Nigeria', code: 'NG', aliases: ['NG'], languages: ['English'] },
  { name: 'North Macedonia', code: 'MK', aliases: ['MK', 'Macedonia'], languages: ['Macedonian'] },
  { name: 'Norway', code: 'NO', aliases: ['NO'], languages: ['Norwegian (Bokmål)'] },
  { name: 'Pakistan', code: 'PK', aliases: ['PK'], languages: ['English', 'Urdu'] },
  { name: 'Panama', code: 'PA', aliases: ['PA'], languages: ['Spanish'] },
  { name: 'Paraguay', code: 'PY', aliases: ['PY'], languages: ['Spanish'] },
  { name: 'Peru', code: 'PE', aliases: ['PE'], languages: ['Spanish'] },
  { name: 'Philippines', code: 'PH', aliases: ['PH'], languages: ['English', 'Tagalog'] },
  { name: 'Poland', code: 'PL', aliases: ['PL'], languages: ['Polish'] },
  { name: 'Portugal', code: 'PT', aliases: ['PT'], languages: ['Portuguese'] },
  { name: 'Romania', code: 'RO', aliases: ['RO'], languages: ['Romanian'] },
  { name: 'Saudi Arabia', code: 'SA', aliases: ['SA', 'KSA'], languages: ['Arabic'] },
  { name: 'Senegal', code: 'SN', aliases: ['SN'], languages: ['French'] },
  { name: 'Serbia', code: 'RS', aliases: ['RS'], languages: ['Serbian'] },
  { name: 'Singapore', code: 'SG', aliases: ['SG'], languages: ['English', 'Chinese (Simplified)'] },
  { name: 'Slovakia', code: 'SK', aliases: ['SK'], languages: ['Slovak'] },
  { name: 'Slovenia', code: 'SI', aliases: ['SI'], languages: ['Slovenian'] },
  { name: 'South Africa', code: 'ZA', aliases: ['ZA', 'RSA'], languages: ['English'] },
  { name: 'South Korea', code: 'KR', aliases: ['KR', 'Korea'], languages: ['Korean'] },
  { name: 'Spain', code: 'ES', aliases: ['ES'], languages: ['Spanish'] },
  { name: 'Sri Lanka', code: 'LK', aliases: ['LK'], languages: ['English'] },
  { name: 'Sweden', code: 'SE', aliases: ['SE'], languages: ['Swedish'] },
  { name: 'Switzerland', code: 'CH', aliases: ['CH'], languages: ['German', 'French', 'Italian'] },
  { name: 'Taiwan', code: 'TW', aliases: ['TW'], languages: ['Chinese (Traditional)'] },
  { name: 'Thailand', code: 'TH', aliases: ['TH'], languages: ['Thai'] },
  { name: 'Tunisia', code: 'TN', aliases: ['TN'], languages: ['Arabic'] },
  { name: 'Turkiye', code: 'TR', aliases: ['TR', 'Turkey'], languages: ['Turkish'] },
  { name: 'Ukraine', code: 'UA', aliases: ['UA'], languages: ['Ukrainian', 'Russian'] },
  { name: 'United Arab Emirates', code: 'AE', aliases: ['AE', 'UAE', 'Emirates'], languages: ['Arabic', 'English'] },
  { name: 'United Kingdom', code: 'GB', aliases: ['GB', 'UK', 'Britain', 'England'], languages: ['English'] },
  { name: 'United States', code: 'US', aliases: ['US', 'USA', 'America'], languages: ['English', 'Spanish'] },
  { name: 'Uruguay', code: 'UY', aliases: ['UY'], languages: ['Spanish'] },
  { name: 'Venezuela', code: 'VE', aliases: ['VE'], languages: ['Spanish'] },
  { name: 'Vietnam', code: 'VN', aliases: ['VN'], languages: ['English', 'Vietnamese'] }
];

// Helper function to get supported languages for a country
export const getLanguagesForCountry = (countryName: string): Language[] => {
  const country = countries.find(c => c.name === countryName);
  if (!country) return [];
  
  return languages.filter(lang => country.languages.includes(lang.name));
};

// Helper function to get countries that support a language
export const getCountriesForLanguage = (languageName: string): Country[] => {
  return countries.filter(country => country.languages.includes(languageName));
};

// Helper function to check if a country-language combination is valid
export const isValidCountryLanguagePair = (countryName: string, languageName: string): boolean => {
  const country = countries.find(c => c.name === countryName);
  return country ? country.languages.includes(languageName) : false;
};
